import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
} from '@mui/material';
import { IconPicker } from '../IconPicker';
import { offlinePlayersApi } from '../../api';
import type { OfflinePlayer } from '../../types';

export interface CreateOfflinePlayerDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (player: OfflinePlayer) => void;
}

export const CreateOfflinePlayerDialog: React.FC<CreateOfflinePlayerDialogProps> = ({
  open,
  onClose,
  onCreated,
}) => {
  const [displayName, setDisplayName] = useState('');
  const [handle, setHandle] = useState('');
  const [iconName, setIconName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setDisplayName('');
    setHandle('');
    setIconName('');
    setError(null);
    onClose();
  };

  const handleCreate = async () => {
    if (!displayName.trim()) {
      setError('Display name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const player = await offlinePlayersApi.createMyOfflinePlayer({
        displayName: displayName.trim(),
        handle: handle.trim() || undefined,
        iconName: iconName.trim() || undefined,
      });
      onCreated(player);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create offline player');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Create Offline Player</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          autoFocus
          fullWidth
          label="Display Name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          disabled={saving}
          sx={{ mt: 1, mb: 2 }}
          required
          placeholder="e.g., John Smith"
          inputProps={{ 'data-testid': 'create-offline-player-name-input' }}
        />
        <TextField
          fullWidth
          label="Handle (4 chars max)"
          value={handle}
          onChange={(e) => setHandle(e.target.value.toUpperCase().slice(0, 4))}
          disabled={saving}
          sx={{ mb: 2 }}
          inputProps={{ maxLength: 4, 'data-testid': 'create-offline-player-handle-input' }}
          helperText="Short identifier shown on squares (e.g., JOHN)"
          placeholder="e.g., JOHN"
        />
        <IconPicker
          value={iconName}
          onChange={setIconName}
          disabled={saving}
          label="Icon"
          helperText="Optional icon shown on squares (overrides handle)"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving} data-testid="create-offline-player-cancel-button">
          Cancel
        </Button>
        <Button onClick={handleCreate} variant="contained" disabled={saving || !displayName.trim()} data-testid="create-offline-player-submit-button">
          {saving ? 'Creating...' : 'Create Player'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
